import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";

import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Wrapper = styled.div`
  padding: 1rem;
`;

const ChartContainer = styled.div`
  height: 70vh;
  padding: 1rem;
`;

const FiltersWrapper = styled.div`
  margin-bottom: 20px;
  display: flex;
  gap: 20px;
  justify-content: center;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 16px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  z-index: 10;

  &:hover {
    color: #000;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ChartWrapper = styled.div`
  position: relative; /* needed for absolute icon */
`;

const monthNamesPL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
];

const IdentyfikacjaNietypowychTransakcji = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [chartData, setChartData] = useState(null);

  const obliczSprzedaz = (row) =>
    parseFloat(
      row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22
    ) || 0;

  useEffect(() => {
    const years = new Set();
    const months = new Set();
    const map = {};

    data.forEach(row => {
      const year = parseInt(row.Rok, 10);
      const month = parseInt(row.Miesiac, 10);
      const sales = obliczSprzedaz(row);

      if (!isNaN(year) && !isNaN(month)) {
        years.add(year);
        months.add(month);
        const key = `${year}-${month}`;
        if (!map[key]) map[key] = 0;
        map[key] += sales;
      }
    });

    const sortedYears = Array.from(years).sort((a, b) => a - b);
    setAvailableYears(sortedYears);

    if (!selectedYear && sortedYears.length > 0) {
      const lastYear = sortedYears[sortedYears.length - 1];
      setSelectedYear(lastYear);

      const candidateMonths = Array.from(months)
        .filter((m) => data.some(d => parseInt(d.Rok, 10) === lastYear && parseInt(d.Miesiac, 10) === m))
        .sort((a, b) => b - a);

      if (candidateMonths.length > 0) {
        setSelectedMonth(candidateMonths[0]);
      }
    }
  }, [data]);

  useEffect(() => {
    if (selectedYear) {
      const months = new Set();
      data.forEach(row => {
        const year = parseInt(row.Rok, 10);
        const month = parseInt(row.Miesiac, 10);
        if (year === selectedYear && !isNaN(month)) {
          months.add(month);
        }
      });
      setAvailableMonths(Array.from(months).sort((a, b) => a - b));
    }
  }, [selectedYear, data]);

  useEffect(() => {
    if (!selectedYear || !selectedMonth) return;

    const dailyMap = {};
    data.forEach(row => {
      const year = parseInt(row.Rok, 10);
      const month = parseInt(row.Miesiac, 10);
      const date = row.DataWystawienia;
      if (year === selectedYear && month === selectedMonth) {
        if (!dailyMap[date]) dailyMap[date] = 0;
        dailyMap[date] += obliczSprzedaz(row);
      }
    });

    const dates = Object.keys(dailyMap).sort();
    const values = dates.map(d => dailyMap[d]);

    const avg = values.reduce((a, b) => a + b, 0) / (values.length || 1);

    // Palette
    const GREEN  = "#2e7d32";
    const RED    = "#d32f2f";
    const BLUE   = "#1976d2";

    // Bars: red if < average, green if >= average
    const backgroundColors = values.map(v =>
      v < avg ? "rgba(211, 47, 47, 0.85)" : "rgba(46, 125, 50, 0.70)"
    );
    const borderColors = values.map(v => (v < avg ? RED : GREEN));

    setChartData({
      labels: dates,
      datasets: [
        {
          label: "Wartość dzienna (PLN)",
          data: values,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
        },
        {
          label: `Średnia: ${avg.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          data: Array(dates.length).fill(avg),
          type: "line",
          borderColor: BLUE,      // average line = blue
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 0,
          fill: false,
        },
      ],
    });
  }, [selectedYear, selectedMonth, data]);

  return (
    <Wrapper>
      <ChartWrapper>
        <InfoIconWrapper data-tooltip-id="anomaly-chart-tooltip">
          <Info />
        </InfoIconWrapper>
        <InfoTooltip
          id="anomaly-chart-tooltip"
          place="left"
          content={
            <>
              <strong>Porównanie średnich dziennych sprzedaży</strong><br />
              Każdy słupek to wartość dzienna; czerwony = poniżej średniej,<br />
              zielony = równa lub powyżej średniej. Linia – średnia miesiąca.
            </>
          }
        />

        <FiltersWrapper>
          <div>
            <label htmlFor="year-select">Rok:</label>
            <select
              id="year-select"
              value={selectedYear || ""}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="month-select">Miesiąc:</label>
            <select
              id="month-select"
              value={selectedMonth || ""}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            >
              {availableMonths.map((month) => (
                <option key={month} value={month}>
                  {monthNamesPL[month - 1]}
                </option>
              ))}
            </select>
          </div>
        </FiltersWrapper>

        <ChartContainer>
          {chartData ? (
            <>
              <Bar
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: `Porównanie średnich dziennych sprzedaży – ${selectedMonth ? monthNamesPL[selectedMonth - 1] : ""} ${selectedYear || ""}`,
                    },
                    legend: { position: "top" },
                    tooltip: {
                      callbacks: {
                        label: (ctx) =>
                          `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString("pl-PL", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`,
                      },
                    },
                  },
                  scales: {
                    y: {
                      min: 0,
                      title: { display: true, text: "Sprzedaż" },
                    },
                  },
                }}
              />

              {/* Custom Legend */}
              <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginTop: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div style={{ width: "16px", height: "16px", backgroundColor: "rgba(211, 47, 47, 0.85)" }}></div>
                  <span> Poniżej średniej</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div style={{ width: "16px", height: "16px", backgroundColor: "rgba(46, 125, 50, 0.70)" }}></div>
                  <span> Równa lub powyżej średniej</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div style={{ width: "16px", height: "2px", backgroundColor: "#1976d2" }}></div>
                  <span> Linia średniej</span>
                </div>
              </div>
            </>
          ) : (
            <p>Ładowanie danych...</p>
          )}
        </ChartContainer>
      </ChartWrapper>
    </Wrapper>
  );
};

export default IdentyfikacjaNietypowychTransakcji;
